import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixFaqQuestionentryV2QuestionEntryServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'bo._base_domain_': [
      {
        srcPath: '/question-entry',
        destPath: '',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/question-entry',
        destPath: '',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/question-entry',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/faq-server/v2/question-entries',
        destPath: '/v2/question-entries',
      },
      {
        srcPath: '/_api/faq-server/v2/draft-question-entries',
        destPath: '/v2/draft-question-entries',
      },
      {
        srcPath: '/_api/faq-server/v2/bulk/question-entries',
        destPath: '/v2/bulk/question-entries',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/faq-server/v2/question-entries',
        destPath: '/v2/question-entries',
      },
      {
        srcPath: '/_api/faq-server/v2/draft-question-entries',
        destPath: '/v2/draft-question-entries',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/faq/v2/question-entries',
        destPath: '/v2/question-entries',
      },
      {
        srcPath: '/faq/v2/draft-question-entries',
        destPath: '/v2/draft-question-entries',
      },
      {
        srcPath: '/faq/question-entry/v2/bulk/question-entries',
        destPath: '/v2/bulk/question-entries',
      },
      {
        srcPath: '/faq/question-entry/v2/draft-question-entries',
        destPath: '/v2/draft-question-entries',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/faq/v2/question-entries',
        destPath: '/v2/question-entries',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/faq-server/v2/question-entries',
        destPath: '/v2/question-entries',
      },
      {
        srcPath: '/_api/faq-server/v2/draft-question-entries',
        destPath: '/v2/draft-question-entries',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/faq-server/v2/question-entries',
        destPath: '/v2/question-entries',
      },
      {
        srcPath: '/_api/faq-server/v2/draft-question-entries',
        destPath: '/v2/draft-question-entries',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/faq-server/v2/question-entries',
        destPath: '/v2/question-entries',
      },
      {
        srcPath: '/_api/faq-server/v2/draft-question-entries',
        destPath: '/v2/draft-question-entries',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/faq-server/v2/question-entries',
        destPath: '/v2/question-entries',
      },
      {
        srcPath: '/_api/faq-server/v2/draft-question-entries',
        destPath: '/v2/draft-question-entries',
      },
    ],
    _: [
      {
        srcPath: '/_api/faq-server/v2/question-entries',
        destPath: '/v2/question-entries',
      },
      {
        srcPath: '/faq/question-entry/v2/bulk/question-entries',
        destPath: '/v2/bulk/question-entries',
      },
      {
        srcPath: '/faq/question-entry/v2/draft-question-entries',
        destPath: '/v2/draft-question-entries',
      },
    ],
    'dev._base_domain_': [
      {
        srcPath: '/_api/faq-server/v2/question-entries',
        destPath: '/v2/question-entries',
      },
      {
        srcPath: '/_api/faq-server/v2/draft-question-entries',
        destPath: '/v2/draft-question-entries',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/faq-server/v2/question-entries',
        destPath: '/v2/question-entries',
      },
      {
        srcPath: '/_api/faq-server/v2/draft-question-entries',
        destPath: '/v2/draft-question-entries',
      },
      {
        srcPath: '/faq/question-entry/v2/bulk/question-entries',
        destPath: '/v2/bulk/question-entries',
      },
      {
        srcPath: '/faq/question-entry/v2/draft-question-entries',
        destPath: '/v2/draft-question-entries',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_faq_question-entry';

/**
 * Creates a question entry with the provided details including question text, answer content, and category assignment.
 * The question entry must be assigned to an existing category and can optionally include labels for additional organization.
 */
export function createQuestionEntry(
  payload: object
): RequestOptionsFactory<any> {
  function __createQuestionEntry({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'questionEntry.createdDate' },
          { path: 'questionEntry.updatedDate' },
          { path: 'questionEntry.richContent.metadata.createdTimestamp' },
          { path: 'questionEntry.richContent.metadata.updatedTimestamp' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'questionEntry.sortOrder' },
          {
            path: 'questionEntry.richContent.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'questionEntry.richContent.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'questionEntry.richContent.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'questionEntry.richContent.nodes.galleryData.options.item.ratio',
          },
          { path: 'questionEntry.richContent.nodes.imageData.image.duration' },
          { path: 'questionEntry.richContent.nodes.imageData.crop.x' },
          { path: 'questionEntry.richContent.nodes.imageData.crop.y' },
          { path: 'questionEntry.richContent.nodes.imageData.crop.width' },
          { path: 'questionEntry.richContent.nodes.imageData.crop.height' },
          { path: 'questionEntry.richContent.nodes.mapData.mapSettings.lat' },
          { path: 'questionEntry.richContent.nodes.mapData.mapSettings.lng' },
          {
            path: 'questionEntry.richContent.nodes.pollData.poll.image.duration',
          },
          {
            path: 'questionEntry.richContent.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'questionEntry.richContent.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.nodes.appEmbedData.image.duration',
          },
          { path: 'questionEntry.richContent.nodes.videoData.video.duration' },
          {
            path: 'questionEntry.richContent.nodes.videoData.thumbnail.duration',
          },
          { path: 'questionEntry.richContent.nodes.audioData.audio.duration' },
          {
            path: 'questionEntry.richContent.nodes.audioData.coverImage.duration',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backdrop.gradient.stops.position',
          },
          { path: 'questionEntry.richContent.nodes.shapeData.shape.duration' },
          {
            path: 'questionEntry.richContent.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
          },
          { path: 'questionEntry.richContent.nodes.tocData.fontSize' },
          { path: 'questionEntry.richContent.nodes.tocData.itemSpacing' },
          {
            path: 'questionEntry.richContent.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          { path: 'questionEntry.labels.sortOrder' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.faq.question_entry.v2.question_entry',
      method: 'POST' as any,
      methodFqn:
        'wix.faq.questionentry.v2.QuestionEntryService.CreateQuestionEntry',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFaqQuestionentryV2QuestionEntryServiceUrl({
        protoPath: '/v2/question-entries',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'questionEntry.createdDate' },
              { path: 'questionEntry.updatedDate' },
              { path: 'questionEntry.richContent.metadata.createdTimestamp' },
              { path: 'questionEntry.richContent.metadata.updatedTimestamp' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'questionEntry.sortOrder' },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'questionEntry.richContent.nodes.imageData.image.duration',
              },
              { path: 'questionEntry.richContent.nodes.imageData.crop.x' },
              { path: 'questionEntry.richContent.nodes.imageData.crop.y' },
              { path: 'questionEntry.richContent.nodes.imageData.crop.width' },
              { path: 'questionEntry.richContent.nodes.imageData.crop.height' },
              {
                path: 'questionEntry.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'questionEntry.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'questionEntry.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              { path: 'questionEntry.richContent.nodes.tocData.fontSize' },
              { path: 'questionEntry.richContent.nodes.tocData.itemSpacing' },
              {
                path: 'questionEntry.richContent.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              { path: 'questionEntry.labels.sortOrder' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createQuestionEntry;
}

/** Retrieves a single question entry by ID. */
export function getQuestionEntry(payload: object): RequestOptionsFactory<any> {
  function __getQuestionEntry({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.faq.question_entry.v2.question_entry',
      method: 'GET' as any,
      methodFqn:
        'wix.faq.questionentry.v2.QuestionEntryService.GetQuestionEntry',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFaqQuestionentryV2QuestionEntryServiceUrl({
        protoPath: '/v2/question-entries/{questionEntryId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'questionEntry.createdDate' },
              { path: 'questionEntry.updatedDate' },
              { path: 'questionEntry.richContent.metadata.createdTimestamp' },
              { path: 'questionEntry.richContent.metadata.updatedTimestamp' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'questionEntry.sortOrder' },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'questionEntry.richContent.nodes.imageData.image.duration',
              },
              { path: 'questionEntry.richContent.nodes.imageData.crop.x' },
              { path: 'questionEntry.richContent.nodes.imageData.crop.y' },
              { path: 'questionEntry.richContent.nodes.imageData.crop.width' },
              { path: 'questionEntry.richContent.nodes.imageData.crop.height' },
              {
                path: 'questionEntry.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'questionEntry.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'questionEntry.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              { path: 'questionEntry.richContent.nodes.tocData.fontSize' },
              { path: 'questionEntry.richContent.nodes.tocData.itemSpacing' },
              {
                path: 'questionEntry.richContent.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              { path: 'questionEntry.labels.sortOrder' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getQuestionEntry;
}

/**
 * Updates a question entry.
 *
 * Each time the question entry is updated, `revision` increments by 1.
 * The current `revision` must be specified when updating the question entry.
 * This ensures you're working with the latest version of a question entry and prevents unintended overwrites.
 *
 * To update multiple question entries, call the Bulk Update Question Entry method.
 *
 * To update only the question entry's `extendedFields` field, call the Update Extended Fields method.
 *
 * To update only the question entry's labels, call the Set Question Entry Labels method.
 */
export function updateQuestionEntry(
  payload: object
): RequestOptionsFactory<any> {
  function __updateQuestionEntry({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'questionEntry.createdDate' },
          { path: 'questionEntry.updatedDate' },
          { path: 'questionEntry.richContent.metadata.createdTimestamp' },
          { path: 'questionEntry.richContent.metadata.updatedTimestamp' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'questionEntry.sortOrder' },
          {
            path: 'questionEntry.richContent.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'questionEntry.richContent.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'questionEntry.richContent.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'questionEntry.richContent.nodes.galleryData.options.item.ratio',
          },
          { path: 'questionEntry.richContent.nodes.imageData.image.duration' },
          { path: 'questionEntry.richContent.nodes.imageData.crop.x' },
          { path: 'questionEntry.richContent.nodes.imageData.crop.y' },
          { path: 'questionEntry.richContent.nodes.imageData.crop.width' },
          { path: 'questionEntry.richContent.nodes.imageData.crop.height' },
          { path: 'questionEntry.richContent.nodes.mapData.mapSettings.lat' },
          { path: 'questionEntry.richContent.nodes.mapData.mapSettings.lng' },
          {
            path: 'questionEntry.richContent.nodes.pollData.poll.image.duration',
          },
          {
            path: 'questionEntry.richContent.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'questionEntry.richContent.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.nodes.appEmbedData.image.duration',
          },
          { path: 'questionEntry.richContent.nodes.videoData.video.duration' },
          {
            path: 'questionEntry.richContent.nodes.videoData.thumbnail.duration',
          },
          { path: 'questionEntry.richContent.nodes.audioData.audio.duration' },
          {
            path: 'questionEntry.richContent.nodes.audioData.coverImage.duration',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.nodes.layoutData.backdrop.gradient.stops.position',
          },
          { path: 'questionEntry.richContent.nodes.shapeData.shape.duration' },
          {
            path: 'questionEntry.richContent.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
          },
          { path: 'questionEntry.richContent.nodes.tocData.fontSize' },
          { path: 'questionEntry.richContent.nodes.tocData.itemSpacing' },
          {
            path: 'questionEntry.richContent.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          { path: 'questionEntry.labels.sortOrder' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.faq.question_entry.v2.question_entry',
      method: 'PATCH' as any,
      methodFqn:
        'wix.faq.questionentry.v2.QuestionEntryService.UpdateQuestionEntry',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFaqQuestionentryV2QuestionEntryServiceUrl({
        protoPath: '/v2/question-entries/{questionEntry.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'questionEntry.createdDate' },
              { path: 'questionEntry.updatedDate' },
              { path: 'questionEntry.richContent.metadata.createdTimestamp' },
              { path: 'questionEntry.richContent.metadata.updatedTimestamp' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'questionEntry.sortOrder' },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'questionEntry.richContent.nodes.imageData.image.duration',
              },
              { path: 'questionEntry.richContent.nodes.imageData.crop.x' },
              { path: 'questionEntry.richContent.nodes.imageData.crop.y' },
              { path: 'questionEntry.richContent.nodes.imageData.crop.width' },
              { path: 'questionEntry.richContent.nodes.imageData.crop.height' },
              {
                path: 'questionEntry.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'questionEntry.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'questionEntry.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              { path: 'questionEntry.richContent.nodes.tocData.fontSize' },
              { path: 'questionEntry.richContent.nodes.tocData.itemSpacing' },
              {
                path: 'questionEntry.richContent.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              { path: 'questionEntry.labels.sortOrder' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateQuestionEntry;
}

/**
 * Deletes a question entry.
 *
 * Deleting a question entry permanently removes it from the site's Wix FAQ dashboard page.
 *
 * To delete multiple question entries, call the Bulk Delete Question Entries method.
 */
export function deleteQuestionEntry(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteQuestionEntry({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.faq.question_entry.v2.question_entry',
      method: 'DELETE' as any,
      methodFqn:
        'wix.faq.questionentry.v2.QuestionEntryService.DeleteQuestionEntry',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFaqQuestionentryV2QuestionEntryServiceUrl({
        protoPath: '/v2/question-entries/{questionEntryId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteQuestionEntry;
}

/**
 * Retrieves a list of question entries, given the specified filters and paging.
 *
 * See [Query Question Entries](https://dev.wix.com/docs/rest/business-management/faq-app/faq/question-entry-v2/query-question-entries) for a list of supported filters.
 *
 * Use the `fieldSet` parameter to control whether sharing links are included in the response.
 */
export function listQuestionEntries(
  payload: object
): RequestOptionsFactory<any> {
  function __listQuestionEntries({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.faq.question_entry.v2.question_entry',
      method: 'GET' as any,
      methodFqn:
        'wix.faq.questionentry.v2.QuestionEntryService.ListQuestionEntries',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFaqQuestionentryV2QuestionEntryServiceUrl({
        protoPath: '/v2/question-entries',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'questionEntries.createdDate' },
              { path: 'questionEntries.updatedDate' },
              { path: 'questionEntries.richContent.metadata.createdTimestamp' },
              { path: 'questionEntries.richContent.metadata.updatedTimestamp' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'questionEntries.sortOrder' },
              {
                path: 'questionEntries.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'questionEntries.richContent.nodes.imageData.image.duration',
              },
              { path: 'questionEntries.richContent.nodes.imageData.crop.x' },
              { path: 'questionEntries.richContent.nodes.imageData.crop.y' },
              {
                path: 'questionEntries.richContent.nodes.imageData.crop.width',
              },
              {
                path: 'questionEntries.richContent.nodes.imageData.crop.height',
              },
              {
                path: 'questionEntries.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'questionEntries.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'questionEntries.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              { path: 'questionEntries.richContent.nodes.tocData.fontSize' },
              { path: 'questionEntries.richContent.nodes.tocData.itemSpacing' },
              {
                path: 'questionEntries.richContent.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              { path: 'questionEntries.labels.sortOrder' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listQuestionEntries;
}

/** Retrieves question entries, given the provided paging, filtering, and sorting. */
export function queryQuestionEntries(
  payload: object
): RequestOptionsFactory<any> {
  function __queryQuestionEntries({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'query.sort.origin.latitude' },
          { path: 'query.sort.origin.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.faq.question_entry.v2.question_entry',
      method: 'POST' as any,
      methodFqn:
        'wix.faq.questionentry.v2.QuestionEntryService.QueryQuestionEntries',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFaqQuestionentryV2QuestionEntryServiceUrl({
        protoPath: '/v2/question-entries/query',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'questionEntries.createdDate' },
              { path: 'questionEntries.updatedDate' },
              { path: 'questionEntries.richContent.metadata.createdTimestamp' },
              { path: 'questionEntries.richContent.metadata.updatedTimestamp' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'questionEntries.sortOrder' },
              {
                path: 'questionEntries.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'questionEntries.richContent.nodes.imageData.image.duration',
              },
              { path: 'questionEntries.richContent.nodes.imageData.crop.x' },
              { path: 'questionEntries.richContent.nodes.imageData.crop.y' },
              {
                path: 'questionEntries.richContent.nodes.imageData.crop.width',
              },
              {
                path: 'questionEntries.richContent.nodes.imageData.crop.height',
              },
              {
                path: 'questionEntries.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'questionEntries.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'questionEntries.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'questionEntries.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              { path: 'questionEntries.richContent.nodes.tocData.fontSize' },
              { path: 'questionEntries.richContent.nodes.tocData.itemSpacing' },
              {
                path: 'questionEntries.richContent.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntries.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntries.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntries.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              { path: 'questionEntries.labels.sortOrder' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryQuestionEntries;
}

/**
 * Deletes multiple question entries.
 *
 * Deleting a question entry permanently removes it from the site's Wix FAQ dashboard page.
 *
 * To delete a single question entry, call the Delete Question Entry method.
 */
export function bulkDeleteQuestionEntries(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeleteQuestionEntries({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.faq.question_entry.v2.question_entry',
      method: 'POST' as any,
      methodFqn:
        'wix.faq.questionentry.v2.QuestionEntryService.BulkDeleteQuestionEntries',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFaqQuestionentryV2QuestionEntryServiceUrl({
        protoPath: '/v2/bulk/question-entries/delete',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteQuestionEntries;
}

/** Updates extended fields of a question entry without incrementing `revision`. */
export function updateExtendedFields(
  payload: object
): RequestOptionsFactory<any> {
  function __updateExtendedFields({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.faq.question_entry.v2.question_entry',
      method: 'POST' as any,
      methodFqn:
        'wix.faq.questionentry.v2.QuestionEntryService.UpdateExtendedFields',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFaqQuestionentryV2QuestionEntryServiceUrl({
        protoPath: '/v2/question-entries/{id}/update-extended-fields',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'questionEntry.createdDate' },
              { path: 'questionEntry.updatedDate' },
              { path: 'questionEntry.richContent.metadata.createdTimestamp' },
              { path: 'questionEntry.richContent.metadata.updatedTimestamp' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'questionEntry.sortOrder' },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'questionEntry.richContent.nodes.imageData.image.duration',
              },
              { path: 'questionEntry.richContent.nodes.imageData.crop.x' },
              { path: 'questionEntry.richContent.nodes.imageData.crop.y' },
              { path: 'questionEntry.richContent.nodes.imageData.crop.width' },
              { path: 'questionEntry.richContent.nodes.imageData.crop.height' },
              {
                path: 'questionEntry.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'questionEntry.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'questionEntry.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              { path: 'questionEntry.richContent.nodes.tocData.fontSize' },
              { path: 'questionEntry.richContent.nodes.tocData.itemSpacing' },
              {
                path: 'questionEntry.richContent.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              { path: 'questionEntry.labels.sortOrder' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateExtendedFields;
}

/**
 * Sets `labels` for a question entry.
 *
 * This method replaces all existing labels for the question entry.
 * To clear all labels, specify an empty labels array.
 */
export function setQuestionEntryLabels(
  payload: object
): RequestOptionsFactory<any> {
  function __setQuestionEntryLabels({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [{ path: 'labels.sortOrder' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.faq.question_entry.v2.question_entry',
      method: 'PATCH' as any,
      methodFqn:
        'wix.faq.questionentry.v2.QuestionEntryService.SetQuestionEntryLabels',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFaqQuestionentryV2QuestionEntryServiceUrl({
        protoPath: '/v2/question-entries/{questionEntryId}/labels',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'questionEntry.createdDate' },
              { path: 'questionEntry.updatedDate' },
              { path: 'questionEntry.richContent.metadata.createdTimestamp' },
              { path: 'questionEntry.richContent.metadata.updatedTimestamp' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'questionEntry.sortOrder' },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'questionEntry.richContent.nodes.imageData.image.duration',
              },
              { path: 'questionEntry.richContent.nodes.imageData.crop.x' },
              { path: 'questionEntry.richContent.nodes.imageData.crop.y' },
              { path: 'questionEntry.richContent.nodes.imageData.crop.width' },
              { path: 'questionEntry.richContent.nodes.imageData.crop.height' },
              {
                path: 'questionEntry.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'questionEntry.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'questionEntry.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              { path: 'questionEntry.richContent.nodes.tocData.fontSize' },
              { path: 'questionEntry.richContent.nodes.tocData.itemSpacing' },
              {
                path: 'questionEntry.richContent.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              { path: 'questionEntry.labels.sortOrder' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __setQuestionEntryLabels;
}

/**
 * Updates multiple question entries.
 *
 * Each time a question entry is updated, its `revision` increments by 1.
 * The current `revision` must be specified when updating the question entry.
 * This ensures you're working with the latest version of a question entry and prevents unintended overwrites.
 *
 * To update a single question entry, call the Update Question Entry method.
 */
export function bulkUpdateQuestionEntry(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateQuestionEntry({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'questionEntries.fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'questionEntries.questionEntry.createdDate' },
          { path: 'questionEntries.questionEntry.updatedDate' },
          {
            path: 'questionEntries.questionEntry.richContent.metadata.createdTimestamp',
          },
          {
            path: 'questionEntries.questionEntry.richContent.metadata.updatedTimestamp',
          },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'questionEntries.questionEntry.sortOrder' },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.imageData.image.duration',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.imageData.crop.x',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.imageData.crop.y',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.imageData.crop.width',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.imageData.crop.height',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.pollData.poll.image.duration',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.appEmbedData.image.duration',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.videoData.video.duration',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.audioData.audio.duration',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.audioData.coverImage.duration',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.shapeData.shape.duration',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.tocData.fontSize',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.tocData.itemSpacing',
          },
          {
            path: 'questionEntries.questionEntry.richContent.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.stops.position',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerX',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerY',
          },
          {
            path: 'questionEntries.questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.stops.position',
          },
          { path: 'questionEntries.questionEntry.labels.sortOrder' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.faq.question_entry.v2.question_entry',
      method: 'POST' as any,
      methodFqn:
        'wix.faq.questionentry.v2.QuestionEntryService.BulkUpdateQuestionEntry',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFaqQuestionentryV2QuestionEntryServiceUrl({
        protoPath: '/v2/bulk/question-entries/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.questionEntry.createdDate' },
              { path: 'results.questionEntry.updatedDate' },
              {
                path: 'results.questionEntry.richContent.metadata.createdTimestamp',
              },
              {
                path: 'results.questionEntry.richContent.metadata.updatedTimestamp',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'results.questionEntry.sortOrder' },
              {
                path: 'results.questionEntry.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'results.questionEntry.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'results.questionEntry.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'results.questionEntry.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'results.questionEntry.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'results.questionEntry.richContent.nodes.imageData.crop.x',
              },
              {
                path: 'results.questionEntry.richContent.nodes.imageData.crop.y',
              },
              {
                path: 'results.questionEntry.richContent.nodes.imageData.crop.width',
              },
              {
                path: 'results.questionEntry.richContent.nodes.imageData.crop.height',
              },
              {
                path: 'results.questionEntry.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'results.questionEntry.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'results.questionEntry.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'results.questionEntry.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'results.questionEntry.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'results.questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'results.questionEntry.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'results.questionEntry.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'results.questionEntry.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'results.questionEntry.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'results.questionEntry.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'results.questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'results.questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'results.questionEntry.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'results.questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.nodes.tocData.fontSize',
              },
              {
                path: 'results.questionEntry.richContent.nodes.tocData.itemSpacing',
              },
              {
                path: 'results.questionEntry.richContent.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.stops.position',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerX',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerY',
              },
              {
                path: 'results.questionEntry.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.stops.position',
              },
              { path: 'results.questionEntry.labels.sortOrder' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateQuestionEntry;
}
